import { useState } from 'react'
import { router } from '@inertiajs/react'
import { RockCategory } from '../../types'

interface Props {
  teamId: number
  categories: RockCategory[]
  onClose: () => void
}

export default function CategoryManager({ teamId, categories, onClose }: Props) {
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const handleCreate = () => {
    if (!newName.trim()) return
    router.post(`/teams/${teamId}/rock_categories`, {
      rock_category: { name: newName.trim() }
    }, {
      preserveScroll: true,
      onSuccess: () => setNewName('')
    })
  }

  const handleUpdate = (id: number) => {
    if (!editName.trim()) return
    router.put(`/teams/${teamId}/rock_categories/${id}`, {
      rock_category: { name: editName.trim() }
    }, {
      preserveScroll: true,
      onSuccess: () => setEditingId(null)
    })
  }

  const handleDelete = (id: number) => {
    if (!confirm('Delete this category? Rocks in this category will become uncategorized.')) return
    router.delete(`/teams/${teamId}/rock_categories/${id}`, { preserveScroll: true })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-text-primary font-heading mb-4">Manage Categories</h3>

        <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
          {categories.length === 0 && (
            <p className="text-sm text-text-muted text-center py-4">No categories yet</p>
          )}
          {categories.map(cat => (
            <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#F5F5F5]/30">
              {editingId === cat.id ? (
                <>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="flex-1 px-2 py-1 border border-stone/30 rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleUpdate(cat.id)
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                  />
                  <button onClick={() => handleUpdate(cat.id)} className="text-sm text-[#1B7340] font-medium">Save</button>
                  <button onClick={() => setEditingId(null)} className="text-sm text-text-muted">Cancel</button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm text-text-primary">{cat.name}</span>
                  <button
                    onClick={() => { setEditingId(cat.id); setEditName(cat.name) }}
                    className="text-sm text-text-muted hover:text-text-primary"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="text-sm text-pinnacle-red/60 hover:text-pinnacle-red"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="New category name"
            className="flex-1 px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
            onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
          />
          <button
            onClick={handleCreate}
            disabled={!newName.trim()}
            className="px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-medium hover:bg-pinnacle-red/90 disabled:opacity-50"
          >
            Add
          </button>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-stone/30 rounded-lg text-sm text-text-secondary hover:bg-[#F8F9FA]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
